// public/js/modules/firestore/firestoreUser.js

import {
    getFirestore,
    doc,
    setDoc,
    updateDoc,
    arrayUnion,
    arrayRemove,
    getDoc,
    collection,
    getDocs,
    query,
    where,
    limit,
    serverTimestamp,
    onSnapshot
} from 'https://www.gstatic.com/firebasejs/9.23.0/firebase-firestore.js';
import { getFunctions, httpsCallable } from "https://www.gstatic.com/firebasejs/9.23.0/firebase-functions.js";
import { getApp } from "https://www.gstatic.com/firebasejs/9.23.0/firebase-app.js";
import { getProfileFromCache, setProfileInCache } from '../userProfileCache.js';
import { setCurrentUserProfile } from '../userState.js';

function isPlainObject(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
    if (value._methodName) return false;
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
}

function flattenForUpdate(obj, prefix = '', res = {}) {
    for (const [key, value] of Object.entries(obj)) {
        const path = prefix ? `${prefix}.${key}` : key;
        if (isPlainObject(value)) {
            flattenForUpdate(value, path, res);
        } else {
            res[path] = value;
        }
    }
    return res;
}

let profileListenerUnsubscribe = null;
let currentListeningUserId = null;


const profileFetchPromises = new Map();

export function listenToUserProfile(userId) {
    if (profileListenerUnsubscribe) {
        if (currentListeningUserId === userId) return; 
        profileListenerUnsubscribe();
    }
    
    if (!userId) return;
    
    const db = getFirestore();
    const userRef = doc(db, 'users', userId);
    currentListeningUserId = userId;
    
    profileListenerUnsubscribe = onSnapshot(userRef, async (docSnap) => {
        if (docSnap.exists()) {
            const normalizedProfileData = mergeWithDotNotation({}, docSnap.data());
            const profileData = { id: docSnap.id, ...normalizedProfileData };
            setProfileInCache(userId, profileData);
            setCurrentUserProfile(profileData);

            const { loadBadgeState } = await import('../badgeState.js');
            await loadBadgeState(profileData);
        }
    });
}

function mergeWithDotNotation(target, updates) {
    const result = { ...target };
    const entries = Object.entries(updates || {});

    
    for (const [key, value] of entries) {
        if (!key.includes('.')) {
            
            if (value && typeof value === 'object' && value._methodName) continue;
            result[key] = value;
        }
    }

    
    for (const [key, value] of entries) {
        if (!key.includes('.')) continue;
        
        
        if (value && typeof value === 'object' && value._methodName) continue;

        const parts = key.split('.');
        let obj = result;
        for (let i = 0; i < parts.length - 1; i++) {
            const part = parts[i];
            obj[part] = (obj[part] && typeof obj[part] === 'object') ? { ...obj[part] } : {};
            obj = obj[part];
        }
        obj[parts[parts.length - 1]] = value;
    }

    return result;
}

function getOrCreateDeviceId() {
  let deviceId = null;
  try {
    deviceId = localStorage.getItem('o11_device_id');
  } catch (e) {}
  if (!deviceId) {
    deviceId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      localStorage.setItem('o11_device_id', deviceId);
    } catch (e) {}
  }
  return deviceId;
}

export async function getUserSession(userId) {
  const db = getFirestore();
  const deviceId = getOrCreateDeviceId();
  const sessionRef = doc(db, 'users', userId, 'sessions', deviceId);
  try {
    const snap = await getDoc(sessionRef);
    return snap.exists() ? snap.data() : null;
  } catch (e) {
    console.error('[getUserSession] Error reading session:', e);
    return null;
  }
}

export async function saveUserSession(userId, data) {
  const db = getFirestore();
  const deviceId = getOrCreateDeviceId();
  const sessionRef = doc(db, 'users', userId, 'sessions', deviceId);
  return setDoc(sessionRef, { ...data, updatedAt: serverTimestamp() }, { merge: true });
}

export async function getUserReadState(userId) {
    const profile = await getUserProfile(userId);
    const readState = profile?.readState || {};
    const dmRaw = readState.dm || {};
    return {
        chatLastReadAt: readState.chatLastReadAt || null,
        dm: typeof dmRaw === 'string' ? (() => { try { return JSON.parse(dmRaw); } catch { return {}; } })() : dmRaw
    };
}

export function saveChatLastRead(userId, iso) {
    return saveUserProfile(userId, { readState: { chatLastReadAt: iso } });
}

export function saveDmReadState(userId, timestamps) {
    return saveUserProfile(userId, { readState: { dm: JSON.stringify(timestamps) } });
}

export async function saveUserProfile(userId, data) {
  const db = getFirestore();
  const userRef = doc(db, 'users', userId);
  const flatData = flattenForUpdate(data);
  const normalizedData = mergeWithDotNotation({}, data);
  
  
  const userProfile = getProfileFromCache(userId) || {};
  setProfileInCache(userId, mergeWithDotNotation({ ...userProfile, id: userId }, data));
  
  try {
      
      return await updateDoc(userRef, { ...flatData, updatedAt: serverTimestamp() });
  } catch (e) {
      
      if (e.code === 'not-found') {
          return await setDoc(userRef, { ...normalizedData, updatedAt: serverTimestamp() }, { merge: true });
      }
      throw e;
  }
}

export async function addFriend(currentUserId, friendToAddId) {
    if (!currentUserId || !friendToAddId || currentUserId === friendToAddId) return;
    const db = getFirestore();
    const userRef = doc(db, 'users', currentUserId);
    return updateDoc(userRef, {
        friends: arrayUnion(friendToAddId),
        contactIds: arrayUnion(friendToAddId)
    });
}

export async function addSavedArtist(currentUserId, artistData) {
    if (!currentUserId || !artistData || !artistData.id) return;
    const db = getFirestore();
    const userRef = doc(db, 'users', currentUserId);
    return updateDoc(userRef, {
        savedArtists: arrayUnion(artistData),
        contactIds: arrayUnion(artistData.id)
    });
}

export async function isUserInContacts(currentUserId, targetUserId) {
    if (!currentUserId || !targetUserId) return false;
    
    const profile = await getUserProfile(currentUserId);
    if (!profile) return false;
    
    const savedArtists = profile.savedArtists || [];
    const friends = profile.friends || [];
    const contactIds = profile.contactIds || [];
    
    const isInSavedArtists = savedArtists.some(artist => artist.id === targetUserId);
    const isInFriends = friends.includes(targetUserId);
    const isInContactIds = contactIds.includes(targetUserId);
    
    return isInSavedArtists || isInFriends || isInContactIds;
}

export async function getSortedContacts(currentUserId = null) {
    let uid = currentUserId;
    if (!uid) {
        const { getAuth } = await import('https://www.gstatic.com/firebasejs/9.23.0/firebase-auth.js');
        uid = getAuth().currentUser?.uid;
    }
    if (!uid) return [];

    const userProfile = await getUserProfile(uid);
    if (!userProfile) return [];

    const friendIds = userProfile.friends || [];
    const savedArtists = userProfile.savedArtists || [];

    const contactIds = Array.from(new Set([
        ...friendIds,
        ...savedArtists.filter(a => a && a.id).map(a => a.id)
    ]));

    const profiles = await Promise.all(
        contactIds.map(id => getUserProfile(id).catch(() => null))
    );

    return profiles
        .filter(p => p)
        .map(p => ({ ...p, id: p.id }))
        .sort((a, b) => (a.artistName || '').localeCompare(b.artistName || ''));
}

export async function toggleDmBlock(currentUserId, targetUserId) {
    if (!currentUserId || !targetUserId || currentUserId === targetUserId) return;
    const profile = await getUserProfile(currentUserId);
    const blocked = (profile?.dmBlockedUserIds || []).includes(targetUserId);
    const db = getFirestore();
    const userRef = doc(db, 'users', currentUserId);
    return updateDoc(userRef, {
        dmBlockedUserIds: blocked ? arrayRemove(targetUserId) : arrayUnion(targetUserId)
    });
}

export async function isDmBlocked(currentUserId, targetUserId) {
    if (!currentUserId || !targetUserId) return false;
    const profile = await getUserProfile(currentUserId);
    return (profile?.dmBlockedUserIds || []).includes(targetUserId);
}

export async function getUserProfile(userId, options = {}) {
    if (!userId || typeof userId !== 'string') return null;

    if (!options.forceServer) {
        const cachedProfile = getProfileFromCache(userId);
        if (cachedProfile) {
            return cachedProfile;
        }

        
        if (profileFetchPromises.has(userId)) {
            return profileFetchPromises.get(userId);
        }
    }

    const fetchPromise = (async () => {
        try {
            const db = getFirestore();
            const userRef = doc(db, 'users', userId);
            const docSnap = options.forceServer
                ? await getDoc(userRef, { source: 'server' })
                : await getDoc(userRef);

            if (docSnap.exists()) {
                const normalizedProfileData = mergeWithDotNotation({}, docSnap.data());
                const profileData = { id: docSnap.id, ...normalizedProfileData };
                setProfileInCache(userId, profileData);
                return profileData;
            }

            try {
                const deletedRef = doc(db, 'deletedUsers', userId);
                const deletedSnap = await getDoc(deletedRef);
                if (deletedSnap.exists()) {
                    const d = deletedSnap.data();
                    const anonName = `Anonymous (${d.artistName || d.username || 'Unknown'})`;
                    const profileData = {
                        id: userId,
                        artistName: anonName,
                        username: anonName,
                        profilePicUrl: '',
                        deletedAt: d.deletedAt || null,
                        isDeleted: true
                    };
                    setProfileInCache(userId, profileData);
                    return profileData;
                }
            } catch (deletedError) {
                console.warn('[getUserProfile] Could not read deletedUsers for', userId, deletedError.message);
            }

            return null;
        } finally {
            profileFetchPromises.delete(userId);
        }
    })();

    const safePromise = fetchPromise.catch(error => {
        console.warn('[getUserProfile] Fetch failed for', userId, error.message);
        return null;
    });

    profileFetchPromises.set(userId, safePromise);
    return safePromise;
}


export async function getUserProfileByEmail(email) {
    if (!email) return null;

    const normalizedEmail = email.trim().toLowerCase();
    const db = getFirestore();

    try {
        const usersQuery = query(
            collection(db, 'users'),
            where('email', '==', normalizedEmail),
            limit(1)
        );
        const snapshot = await getDocs(usersQuery);

        if (!snapshot.empty) {
            const docSnap = snapshot.docs[0];
            const normalizedProfileData = mergeWithDotNotation({}, docSnap.data());
            const profileData = { id: docSnap.id, ...normalizedProfileData };
            setProfileInCache(docSnap.id, profileData);
            return profileData;
        }
    } catch (error) {
        console.error('[firestoreUser] Error fetching user profile by email:', error);
    }

    return null;
}

export async function getAllUsers() {
    const db = getFirestore();
    const usersCol = collection(db, 'users');
    const snapshot = await getDocs(usersCol);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}


export async function isArtistNameTaken(name, excludeUserId = null) {
    if (!name || name.trim().length < 2) return false;
    
    const normalizedName = name.trim().toLowerCase();
    const allUsers = await getAllUsers();
    
    return allUsers.some(user => {
        if (excludeUserId && user.id === excludeUserId) return false;
        return user.artistName && user.artistName.toLowerCase() === normalizedName;
    });
}

export async function getUserLotteryEntry(userId) {
    const db = getFirestore();
    const entryRef = doc(db, 'users', userId, 'lottery', 'entry');
    const docSnap = await getDoc(entryRef);
    
    return docSnap;
}


export async function saveLotteryEntry(payload) {
    const functions = getFunctions(getApp(), 'us-east1');
    const saveEntry = httpsCallable(functions, 'saveLotteryEntry');
    return saveEntry(payload);
}


export async function removeLotteryEntry() {
    const functions = getFunctions(getApp(), 'us-east1');
    const removeEntry = httpsCallable(functions, 'removeLotteryEntry');
    return removeEntry();
}